/**
 * SPEC-AdminImpersonation — client lib for the three admin endpoints.
 *
 * Each function calls the matching server endpoint and invalidates the two
 * auth queries (`/api/auth/user` and `/api/auth/check-approved`) so the
 * banner, the `useRole()` context, and every route gate update on the next
 * render.
 *
 * SPEC §"End-to-end chain coverage" race fix (2026-05-29) — after the POST
 * succeeds and the queries are invalidated, `startImpersonation` forces a
 * HARD navigation to the target role's landing page. This kills the
 * cache-staleness race that previously bounced Sarah's `/qa/agent` to
 * `/qa/list` before RoleContext could refresh. `stopImpersonation` gets
 * the same treatment for symmetry — a full page reload on exit guarantees
 * the next render sees the real session role, not the stale impersonated
 * one. The launcher's `onSuccess` MUST NOT navigate — the lib owns the
 * transition end-to-end.
 *
 * Callers (banner / launcher / hook) wrap these in `useMutation` for
 * loading / error state; the raw functions are kept side-effect-free
 * beyond the fetch + query invalidation + hard nav so tests can swap them.
 */

import type { QueryClient } from "@tanstack/react-query";

export type ImpersonationTargetRole = "agent" | "senior";

export interface ImpersonationResponse {
  impersonating: boolean;
  effectiveRole?: ImpersonationTargetRole;
  actualRole?: string;
  actualUserId?: string;
  writesEnabled?: boolean;
}

// SPEC-AdminImpersonation race fix — target role → landing page mapping.
// Exported so tests + the launcher reference the same constants. The
// senior landing is the QA list at `/qa/list`; the agent landing is the
// agent UI at `/qa/agent`.
export const IMPERSONATION_LANDING: Record<ImpersonationTargetRole, string> = {
  agent: "/qa/agent",
  senior: "/qa/list",
};

// SPEC-AdminImpersonation race fix — when impersonation is cleared, fall
// back to "/" and let the route guards send the user to their real-role
// landing. The server already knows actualRole from the session, so the
// next request to /api/auth/check-approved returns the real role.
export const EXIT_LANDING = "/";

async function parseJsonOrThrow(res: Response): Promise<any> {
  const body = await res.text();
  let parsed: any;
  try {
    parsed = body ? JSON.parse(body) : {};
  } catch {
    parsed = { message: body };
  }
  if (!res.ok) {
    const err: any = new Error(parsed?.message ?? parsed?.error ?? res.statusText);
    err.status = res.status;
    err.code = parsed?.error;
    err.body = parsed;
    throw err;
  }
  return parsed;
}

async function invalidateAuth(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/auth/check-approved"] }),
  ]);
}

// Force a hard navigation. Pulled into its own helper so tests can spy.
function hardNavigate(href: string): void {
  // jsdom + vitest can re-define window.location; production browsers honor
  // the assignment as a top-level navigation that reloads the SPA. The reload
  // is the whole point — it guarantees the next render reads fresh auth state.
  // Node test environments without jsdom may not define `window` — guard so
  // the lib remains importable in non-DOM contexts.
  if (typeof window !== "undefined" && window.location) {
    window.location.href = href;
  }
}

export async function startImpersonation(
  queryClient: QueryClient,
  targetRole: ImpersonationTargetRole,
): Promise<ImpersonationResponse> {
  const res = await fetch("/api/admin/impersonate", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetRole }),
  });
  const data = await parseJsonOrThrow(res);
  await invalidateAuth(queryClient);
  // SPEC §"End-to-end chain coverage" item 2 — hard navigate to the target's
  // landing page. Kills the cache-staleness race in one stroke: by the time
  // the new page renders, `/api/auth/check-approved` has been refetched with
  // the impersonated role baked into the session cookie.
  hardNavigate(IMPERSONATION_LANDING[targetRole]);
  return data;
}

export async function toggleWrites(
  queryClient: QueryClient,
  writesEnabled: boolean,
): Promise<ImpersonationResponse> {
  const res = await fetch("/api/admin/impersonate", {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ writesEnabled }),
  });
  const data = await parseJsonOrThrow(res);
  await invalidateAuth(queryClient);
  return data;
}

export async function stopImpersonation(
  queryClient: QueryClient,
): Promise<ImpersonationResponse> {
  const res = await fetch("/api/admin/impersonate", {
    method: "DELETE",
    credentials: "include",
  });
  const data = await parseJsonOrThrow(res);
  await invalidateAuth(queryClient);
  // SPEC §"End-to-end chain coverage" item 5 — hard navigate back to "/".
  // The server already knows actualRole from the session; route guards do
  // the rest. A full reload guarantees no stale cached `role` survives.
  hardNavigate(EXIT_LANDING);
  return data;
}
